import { FormattedText } from "@tm/static";
import { useMemo } from "react";
import type {
  CancelPolicySupportContactType,
  CancelPolicySupportInfoData,
} from "../types";
import {
  cardHeaderTitleStyle,
  StepCard,
  SupportContactLink,
  SupportContactList,
  SupportInfoContent,
  SupportInfoHeader,
  supportSubTitleStyle,
} from "./CancelPolicyStepCards.style";

const sanitizePhoneValue = (value?: string) =>
  (value ?? "").replace(/[^\d+]/g, "");

const buildHref = (
  type: CancelPolicySupportContactType | undefined,
  rawValue?: string,
  rawLabel?: string
): string | null => {
  const value = (rawValue ?? rawLabel ?? "").trim();
  if (!value) return null;

  if (type === "PHONE") {
    const phone = sanitizePhoneValue(value);
    return phone ? `tel:${phone}` : null;
  }

  if (type === "EMAIL") return `mailto:${value}`;

  return null;
};

export const CancelPolicySupportInfo = ({
  header,
  contactOptions,
}: CancelPolicySupportInfoData) => {
  const visibleContacts = useMemo(
    () =>
      (contactOptions ?? []).filter((option) => option.label || option.value),
    [contactOptions]
  );

  if (!header?.title && !header?.subTitle && !visibleContacts.length) {
    return null;
  }

  return (
    <StepCard>
      <SupportInfoContent>
        {header?.title || header?.subTitle ? (
          <SupportInfoHeader>
            {header?.title ? (
              <FormattedText
                value={header.title}
                baseStyle={cardHeaderTitleStyle}
              />
            ) : null}
            {header?.subTitle ? (
              <FormattedText
                value={header.subTitle}
                baseStyle={supportSubTitleStyle}
              />
            ) : null}
          </SupportInfoHeader>
        ) : null}

        {visibleContacts.length ? (
          <SupportContactList>
            {visibleContacts.map((option, index) => {
              const displayLabel = option.label || option.value || "";
              const href = buildHref(option.type, option.value, option.label);

              if (!href) {
                return (
                  <SupportContactLink
                    as="span"
                    key={`${option.type ?? "contact"}-${index}`}
                  >
                    {displayLabel}
                  </SupportContactLink>
                );
              }

              return (
                <SupportContactLink
                  key={`${option.type ?? "contact"}-${index}`}
                  href={href}
                >
                  {displayLabel}
                </SupportContactLink>
              );
            })}
          </SupportContactList>
        ) : null}
      </SupportInfoContent>
    </StepCard>
  );
};

export default CancelPolicySupportInfo;
